import { keepPreviousData, useQuery } from "@tanstack/react-query";

import type { CountryCode } from "../../../types/common";

export interface ICompanySearchItem {
  companyName: string;
  organisationNumber: string;
}

interface ICompanySearchResult {
  results: ICompanySearchItem[];
  hasDuplicates: boolean;
}

const shouldEnableSearch = (
  countryCode: CountryCode,
  query?: string,
): boolean => {
  if (!query) return false;

  const trimmedQuery = query.replace(/-/g, "");
  const isOnlyNumbers = /^\d+$/.test(trimmedQuery);

  switch (countryCode) {
    case "SE":
      return isOnlyNumbers ? trimmedQuery.length >= 6 : query.length >= 3;
    case "FI":
      return isOnlyNumbers ? trimmedQuery.length >= 1 : query.length >= 2;
    default:
      return false;
  }
};

const fetchCompanySearch = async (
  backendBaseUrl: string,
  countryCode: CountryCode,
  query: string,
  signal?: AbortSignal,
): Promise<ICompanySearchResult> => {
  const searchQuery = query.replace(/-/g, "");
  const url = `${backendBaseUrl}/company-data/api/v1/company-search?countryCode=${countryCode}&query=${encodeURIComponent(searchQuery)}`;

  const response = await fetch(url, { signal });

  if (!response.ok) {
    throw new Error(`Company search failed: ${response.status}`);
  }

  const results: ICompanySearchItem[] = await response.json();

  const hasDuplicates =
    results
      .slice()
      .map((c) => c.companyName.toLowerCase())
      .filter((v, i, a) => a.indexOf(v) !== i).length > 0;

  return { results, hasDuplicates };
};

export const useCompanySearch = (
  backendBaseUrl: string,
  countryCode: CountryCode,
  query?: string,
) => {
  return useQuery({
    queryKey: ["COMPANY_SEARCH_SHARED", backendBaseUrl, countryCode, query],
    queryFn: ({ signal }) =>
      fetchCompanySearch(backendBaseUrl, countryCode, query ?? "", signal),
    enabled: shouldEnableSearch(countryCode, query),
    placeholderData: keepPreviousData,
    staleTime: Number.POSITIVE_INFINITY,
  });
};
